/** UI consumers only know this contract. Camera and synthetic capture are interchangeable. */
export type CaptureSource = { stream: MediaStream; dispose: () => void };
export type CaptureAdapter = {
  open: (facing: "user" | "environment") => Promise<CaptureSource>;
};
/** Library metadata; encoded video is loaded separately for review and export. */
export type TakeSummary = {
  id: string;
  created: number;
  title: string;
  duration: number;
};
export type Recording = TakeSummary & { blob: Blob };

export const cameraAdapter: CaptureAdapter = {
  async open(facing) {
    if (!navigator.mediaDevices?.getUserMedia)
      throw new Error(
        "Camera access needs HTTPS or localhost and a supported browser.",
      );
    const video: MediaTrackConstraints & { resizeMode: ConstrainDOMString } = {
      // Desktop webcams may not report a facing mode. Keep the default flexible,
      // but never silently substitute the front camera when requesting the back.
      facingMode:
        facing === "environment" ? { exact: facing } : { ideal: facing },
      resizeMode: { exact: "none" },
      // Keep the browser's default camera mode within a 1080p ceiling.
      // No ideal size: do not force cameras up to their maximum resolution.
      width: { max: 1920 },
      height: { max: 1080 },
    };
    const stream = await navigator.mediaDevices.getUserMedia({
      video,
      audio: true,
    });
    return {
      stream,
      dispose() {
        for (const track of stream.getTracks()) track.stop();
      },
    };
  },
};

// A real, recordable canvas stream. No device permissions, fake success, or fake video files.
export const demoAdapter: CaptureAdapter = {
  async open() {
    const canvas = document.createElement("canvas");
    canvas.width = 720;
    canvas.height = 1280;
    const context = canvas.getContext("2d")!;
    let frame = 0;
    const started = performance.now();
    function paint() {
      context.fillStyle = "#263c35";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.strokeStyle = "#425c50";
      context.lineWidth = 2;
      for (let x = 0; x < canvas.width; x += 80) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x, canvas.height);
        context.stroke();
      }
      context.fillStyle = "#e5efcf";
      context.font = "32px sans-serif";
      context.textAlign = "center";
      context.fillText("CUE / CAMERA TEST", 360, 520);
      context.font = "22px monospace";
      const seconds = ((performance.now() - started) / 1000).toFixed(1);
      context.fillText(`${seconds}s · no camera or microphone`, 360, 570);
      frame = requestAnimationFrame(paint);
    }
    paint();
    const stream = canvas.captureStream(30);
    return {
      stream,
      dispose() {
        cancelAnimationFrame(frame);
        for (const track of stream.getTracks()) track.stop();
      },
    };
  },
};

export function createRecorder(
  stream: MediaStream,
  onStarted: () => void,
  onEnded: (blob: Blob) => void,
  onError: (message: string) => void,
): MediaRecorder {
  if (typeof MediaRecorder === "undefined")
    throw new Error(
      "Video recording is unavailable in this browser. Try Safari or Chrome.",
    );
  const formats = [
    // Let the encoder choose a profile and level for the camera's native size.
    // A supported fixed H.264 level may not suit the current camera mode.
    "video/mp4",
    "video/webm;codecs=vp9,opus",
    "video/webm",
  ];
  const mimeType = formats.find(function (type) {
    return MediaRecorder.isTypeSupported(type);
  });
  const recorder = new MediaRecorder(
    stream,
    mimeType ? { mimeType } : undefined,
  );
  const chunks: Blob[] = [];
  recorder.ondataavailable = function (event) {
    if (event.data.size) chunks.push(event.data);
  };
  recorder.onstart = onStarted;
  recorder.onerror = function () {
    onError(
      "Recording was interrupted. Check the recovered take before recording again.",
    );
  };
  recorder.onstop = function () {
    onEnded(new Blob(chunks, { type: recorder.mimeType }));
  };
  return recorder;
}

export function recordingFile(take: Recording): File {
  const extension = take.blob.type.includes("mp4") ? "mp4" : "webm";
  const filename = `cue-${take.created}.${extension}`;
  return new File([take.blob], filename, { type: take.blob.type });
}

export async function shareRecording(
  take: Recording,
): Promise<"shared" | "downloaded"> {
  const file = recordingFile(take);
  if (navigator.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title: take.title });
    return "shared";
  }
  downloadRecording(take);
  return "downloaded";
}

export function downloadRecording(take: Recording): void {
  const file = recordingFile(take);
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  link.click();
  setTimeout(function () {
    URL.revokeObjectURL(url);
  }, 60000);
}
